import { Building2, Check, ChevronDown, Plus } from "lucide-react";
import {
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

export interface ConsoleOrganizationOption {
  id: string;
  name: string;
  canManageSecrets?: boolean;
}

const AVATAR_TONES = [
  "bg-[var(--theme-organization-avatar-0-background)] text-[var(--theme-organization-avatar-0-foreground)]",
  "bg-[var(--theme-organization-avatar-1-background)] text-[var(--theme-organization-avatar-1-foreground)]",
  "bg-[var(--theme-organization-avatar-2-background)] text-[var(--theme-organization-avatar-2-foreground)]",
  "bg-[var(--theme-organization-avatar-3-background)] text-[var(--theme-organization-avatar-3-foreground)]",
  "bg-[var(--theme-organization-avatar-4-background)] text-[var(--theme-organization-avatar-4-foreground)]",
  "bg-[var(--theme-organization-avatar-5-background)] text-[var(--theme-organization-avatar-5-foreground)]",
];

function avatarTone(value: string): number {
  let hash = 0;
  for (const character of value) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }
  return hash % AVATAR_TONES.length;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[words.length - 1]![0]}`.toUpperCase();
}

function OrganizationAvatar({ name }: { name: string }) {
  return (
    <span
      aria-hidden="true"
      className={`grid size-8 shrink-0 place-items-center rounded-lg text-xs font-semibold ${AVATAR_TONES[avatarTone(name)]}`}
    >
      {initials(name)}
    </span>
  );
}

export function OrganizationSwitcher({
  organizationId,
  organizations,
  onChange,
  onNavigate,
}: {
  organizationId?: string;
  organizations: readonly ConsoleOrganizationOption[];
  onChange: (organizationId: string) => void;
  onNavigate?: (event: MouseEvent<HTMLAnchorElement>, href: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const menuId = useId();

  const selected = organizations.find(
    (organization) => organization.id === organizationId,
  );
  const selectedIndex = organizations.findIndex(
    (organization) => organization.id === organizationId,
  );
  const triggerName = selected?.name ?? (organizationId ? "Organization" : "No organization");
  const itemCount = organizations.length + 1;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: globalThis.MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) itemRefs.current[activeIndex]?.focus();
  }, [open]); // Focus only when the menu opens; arrows move focus explicitly.

  function openMenu() {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  function closeMenu({ restoreFocus = false } = {}) {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }

  function selectOrganization(organization: ConsoleOrganizationOption) {
    onChange(organization.id);
    closeMenu({ restoreFocus: true });
  }

  function moveActive(delta: number) {
    const next = (activeIndex + delta + itemCount) % itemCount;
    setActiveIndex(next);
    itemRefs.current[next]?.focus();
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu({ restoreFocus: true });
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(-1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
      itemRefs.current[0]?.focus();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(itemCount - 1);
      itemRefs.current[itemCount - 1]?.focus();
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Organization: ${triggerName}`}
        className="pointer-events-auto flex h-10 max-w-[calc(100vw-8rem)] items-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-1.5 text-left text-sm shadow-sm transition hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] sm:max-w-xs sm:px-2"
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={(event) => {
          if (open && event.key === "Escape") {
            event.preventDefault();
            closeMenu({ restoreFocus: true });
            return;
          }
          if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            openMenu();
          }
        }}
        ref={triggerRef}
        title={triggerName}
        type="button"
      >
        {selected ? (
          <OrganizationAvatar name={selected.name} />
        ) : (
          <span
            aria-hidden="true"
            className="grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--muted)] text-[var(--muted-foreground)]"
          >
            <Building2 className="size-4" />
          </span>
        )}
        <span className="hidden min-w-0 flex-1 truncate font-medium sm:block">
          {triggerName}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`size-4 shrink-0 text-[var(--muted-foreground)] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div
          aria-label="Organizations"
          className="pointer-events-auto absolute top-[calc(100%+0.5rem)] right-0 z-50 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] p-1.5 shadow-lg"
          id={menuId}
          onKeyDown={onMenuKeyDown}
          role="menu"
        >
          <div className="max-h-80 overflow-y-auto">
            {organizations.map((organization, index) => {
              const isSelected = organization.id === organizationId;
              return (
                <button
                  aria-checked={isSelected}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left text-sm transition hover:bg-[var(--muted)] focus:bg-[var(--muted)] focus:outline-none ${activeIndex === index ? "bg-[var(--muted)]" : ""}`}
                  key={organization.id}
                  onClick={() => selectOrganization(organization)}
                  onMouseEnter={() => setActiveIndex(index)}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  role="menuitemradio"
                  tabIndex={-1}
                  type="button"
                >
                  <OrganizationAvatar name={organization.name} />
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {organization.name}
                  </span>
                  {isSelected ? (
                    <Check
                      aria-hidden="true"
                      className="size-4 shrink-0 text-[var(--action)]"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>

          <a
            className={`mt-1 flex items-center gap-2.5 rounded-xl border-t border-[var(--border)] px-2 py-2 text-sm transition hover:bg-[var(--muted)] focus:bg-[var(--muted)] focus:outline-none ${activeIndex === organizations.length ? "bg-[var(--muted)]" : ""}`}
            href="/organizations/new"
            onClick={(event) => {
              closeMenu();
              onNavigate?.(event, "/organizations/new");
            }}
            onMouseEnter={() => setActiveIndex(organizations.length)}
            ref={(node) => {
              itemRefs.current[organizations.length] = node;
            }}
            role="menuitem"
            tabIndex={-1}
          >
            <span
              aria-hidden="true"
              className="grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--action)] text-[var(--action-foreground)]"
            >
              <Plus className="size-4" />
            </span>
            <span className="min-w-0 flex-1 truncate font-medium">
              Create Organization
            </span>
          </a>
        </div>
      ) : null}
    </div>
  );
}
